import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Upload, 
  Brain, 
  FileCheck, 
  BarChart3, 
  Shield, 
  Zap, 
  Check,
  Building2,
  Menu,
  X,
  Lock,
  Globe,
  Clock,
  HelpCircle,
  Wallet,
  MessageCircle,
  Mic,
  Smartphone,
  Camera,
  RefreshCw,
  Sparkles,
  TrendingUp,
  Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { SocialProofStrip, DemoSection, SavingsCalculator, ChatWidget } from '../components/PLGComponents';
import { ChevronDown, User, Briefcase } from 'lucide-react';

/* ─── shared constants ─── */
const heading = "font-extrabold tracking-tight";
const hFont = { fontFamily: 'Manrope, sans-serif' };
const monoFont = { fontFamily: "'JetBrains Mono', monospace" };

/* ─── Section Label (reused in every section) ─── */
function SectionLabel({ icon: Icon, children, dark }) {
  return (
    <div className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 mb-5 ${
      dark ? 'bg-white/[0.06] border border-white/[0.08]' : 'bg-[#D94E18]/[0.06]'
    }`}>
      <Icon className="w-3.5 h-3.5 text-[#D94E18]" />
      <span className={`text-xs font-bold uppercase tracking-wider ${dark ? 'text-white' : 'text-[#D94E18]'}`}>
        {children}
      </span>
    </div>
  );
}

/* ─── Card (reused in AI Intelligence + Features) ─── */
function FeatureCard({ icon: Icon, title, desc, accent }) {
  if (accent) {
    return (
      <div data-testid={`card-${title.slice(0,20).replace(/\s/g,'-').toLowerCase()}`}
        className="group p-6 rounded-2xl bg-stone-900 text-white border border-stone-800 hover:border-[#D94E18]/40 transition-all duration-300">
        <div className="w-10 h-10 rounded-xl bg-[#D94E18] flex items-center justify-center mb-4">
          <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
        </div>
        <h3 className="text-sm font-bold text-white mb-2" style={hFont}>{title}</h3>
        <p className="text-sm text-stone-400 leading-relaxed">{desc}</p>
      </div>
    );
  }
  return (
    <div data-testid={`card-${title.slice(0,20).replace(/\s/g,'-').toLowerCase()}`}
      className="group p-6 rounded-2xl border border-stone-200 bg-white hover:border-[#D94E18]/20 hover:shadow-md transition-all duration-300">
      <div className="w-10 h-10 rounded-xl bg-[#D94E18]/[0.08] flex items-center justify-center mb-4 group-hover:bg-[#D94E18]/[0.12] transition-colors">
        <Icon className="w-5 h-5 text-[#D94E18]" strokeWidth={1.5} />
      </div>
      <h3 className="text-sm font-bold text-stone-900 mb-2" style={hFont}>{title}</h3>
      <p className="text-sm text-stone-600 leading-relaxed">{desc}</p>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   NAVBAR
   ══════════════════════════════════════════════════ */
function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdown, setDropdown] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdown) return;
    const handler = () => setDropdown(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [dropdown]);

  return (
    <nav data-testid="landing-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2.5" data-testid="landing-logo">
          <div className="w-8 h-8 bg-[#D94E18] rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="text-lg font-extrabold text-stone-900" style={hFont}>kaya.ai</span>
        </Link>

        <div className="hidden md:flex items-center gap-7">
          <a href="#demo" className="text-sm font-semibold text-[#D94E18] hover:text-[#C2410C] transition-colors" data-testid="nav-try-demo">Try Demo</a>

          {/* Direct nav links */}
          <a href="#savings" className="text-sm font-semibold text-stone-600 hover:text-stone-900 transition-colors" data-testid="nav-savings-calc">Your Time Back</a>

          {/* Solutions dropdown */}
          <div className="relative">
            <button onClick={(e) => { e.stopPropagation(); setDropdown(dropdown === 'solutions' ? null : 'solutions'); }} className="text-sm font-semibold text-stone-600 hover:text-stone-900 transition-colors flex items-center gap-1" data-testid="nav-solutions">
              Solutions <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {dropdown === 'solutions' && (
              <div className="absolute top-full left-0 mt-2 bg-white rounded-xl border border-zinc-200 shadow-lg py-2 min-w-[220px]">
                <Link to="/for/solo-founders" onClick={() => setDropdown(null)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-50 transition-colors" data-testid="nav-solo-founders">
                  <User className="w-4 h-4 text-[#D94E18]" />
                  <div><p className="text-sm font-medium text-zinc-900">Solo Founders & Side Hustlers</p><p className="text-xs text-zinc-400">Your books, sorted in minutes</p></div>
                </Link>
                <Link to="/for/startups" onClick={() => setDropdown(null)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-50 transition-colors" data-testid="nav-startups">
                  <Sparkles className="w-4 h-4 text-[#D94E18]" />
                  <div><p className="text-sm font-medium text-zinc-900">Startups & Scale-ups</p><p className="text-xs text-zinc-400">Move fast, stay compliant</p></div>
                </Link>
                <Link to="/for/growing-teams" onClick={() => setDropdown(null)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-50 transition-colors" data-testid="nav-growing-teams">
                  <Building2 className="w-4 h-4 text-[#D94E18]" />
                  <div><p className="text-sm font-medium text-zinc-900">Growing Teams & SMEs</p><p className="text-xs text-zinc-400">Scale without scaling costs</p></div>
                </Link>
                <Link to="/for/finance-pros" onClick={() => setDropdown(null)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-50 transition-colors" data-testid="nav-finance-pros">
                  <Briefcase className="w-4 h-4 text-[#D94E18]" />
                  <div><p className="text-sm font-medium text-zinc-900">Finance Professionals</p><p className="text-xs text-zinc-400">10x your client capacity</p></div>
                </Link>
              </div>
            )}
          </div>

          <Link to="/blog" className="text-sm font-semibold text-stone-600 hover:text-stone-900 transition-colors" data-testid="nav-blog">Blog</Link>
          <a href="#pricing" className="text-sm font-semibold text-stone-600 hover:text-stone-900 transition-colors">Pricing</a>
          <Link to="/login" data-testid="landing-login-btn" className="text-sm font-semibold text-stone-600 hover:text-stone-900 transition-colors">Sign in</Link>
          <Link to="/login?register=true" data-testid="landing-get-started-btn">
            <Button className="bg-[#D94E18] hover:bg-[#C2410C] text-white h-9 px-5 text-sm font-semibold rounded-full">
              Get started <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>

        <button className="md:hidden p-2 text-stone-600" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-stone-100 shadow-lg">
          <div className="px-6 py-4 space-y-1">
            <a href="#demo" onClick={() => setMobileOpen(false)} className="block py-2.5 text-sm font-semibold text-[#D94E18]">Try Demo</a>
            <a href="#savings" onClick={() => setMobileOpen(false)} className="block py-2.5 text-sm font-semibold text-stone-600">Your Time Back</a>
            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider pt-2">Solutions</p>
            <Link to="/for/solo-founders" onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-stone-600 pl-2">Solo Founders & Side Hustlers</Link>
            <Link to="/for/startups" onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-stone-600 pl-2">Startups & Scale-ups</Link>
            <Link to="/for/growing-teams" onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-stone-600 pl-2">Growing Teams & SMEs</Link>
            <Link to="/for/finance-pros" onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-stone-600 pl-2">Finance Professionals</Link>
            <div className="border-t border-zinc-100 pt-2 mt-2">
              <Link to="/blog" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-medium text-stone-700">Blog</Link>
              <a href="#pricing" onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-stone-600">Pricing</a>
              <Link to="/login" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-medium text-stone-700">Sign in</Link>
            </div>
            <Link to="/login?register=true">
              <Button className="w-full bg-[#D94E18] hover:bg-[#C2410C] text-white h-10 rounded-full mt-2">
                Get started <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

/* ══════════════════════════════════════════════════
   HERO
   ══════════════════════════════════════════════════ */
function HeroSection() {
  return (
    <section data-testid="hero-section" className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
      <div className="absolute inset-0 bg-[#FAFAF9]">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-[#D94E18]/[0.03] blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-stone-900 text-white rounded-full px-3.5 py-1.5 mb-6">
              <Brain className="w-3.5 h-3.5 text-[#D94E18]" />
              <span className="text-xs font-semibold">AI-powered accounting</span>
            </div>

            <h1 className={`text-4xl sm:text-5xl lg:text-[3.25rem] ${heading} text-stone-900 leading-[1.1]`} style={hFont} data-testid="hero-headline">
              Your AI accountant that{' '}
              <span className="text-[#D94E18]">learns your business</span>
            </h1>

            <p className="mt-6 text-base md:text-lg text-stone-700 max-w-xl leading-relaxed">
              Upload a bank statement and Kaya's AI reads every transaction, classifies it, applies the right tax rules, and produces
              financial reports — all while <strong className="text-stone-900">learning from your corrections</strong> to get smarter over time.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-start gap-3">
              <Link to="/login?register=true" data-testid="hero-cta-btn">
                <Button className="bg-[#D94E18] hover:bg-[#C2410C] text-white h-12 px-8 text-sm font-semibold rounded-full shadow-lg shadow-[#D94E18]/20 transition-all hover:shadow-xl hover:shadow-[#D94E18]/25">
                  Get started <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#demo">
                <Button variant="outline" className="h-12 px-8 text-sm font-semibold rounded-full border-stone-300 text-stone-700 hover:bg-stone-50">
                  Try uploading your bank statement <Upload className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
            <a href="#savings" className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-[#D94E18] hover:text-[#C2410C] transition-colors group" data-testid="hero-savings-link">
              What's your time really worth? <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>

          {/* Right: AI Pipeline Visual */}
          <div className="hidden lg:flex flex-col items-center justify-center" data-testid="hero-illustration">
            <div className="relative flex items-start gap-3">
              <div className="w-44 bg-white rounded-2xl border border-stone-200 shadow-sm p-4">
                <div className="absolute -top-3 left-[4.5rem] w-7 h-7 rounded-full bg-stone-900 text-white flex items-center justify-center text-xs font-bold shadow-sm z-10" data-testid="step-1">1</div>
                <div className="w-8 h-8 rounded-xl bg-[#D94E18]/10 flex items-center justify-center mb-2.5">
                  <Upload className="w-4 h-4 text-[#D94E18]" />
                </div>
                <p className="text-[11px] font-bold text-stone-900 mb-0.5">Upload</p>
                <p className="text-[9px] text-stone-500 mb-2.5">PDF, CSV, or image</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-[9px] text-stone-600 bg-stone-50 rounded px-2 py-1">
                    <FileCheck className="w-3 h-3 text-[#D94E18] flex-shrink-0" /> statement.pdf
                  </div>
                  <div className="flex items-center gap-1.5 text-[9px] text-stone-600 bg-stone-50 rounded px-2 py-1">
                    <Camera className="w-3 h-3 text-[#D94E18] flex-shrink-0" /> receipt photo
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center justify-center self-center gap-1">
                <div className="w-8 h-px bg-stone-300" />
                <ArrowRight className="w-4 h-4 text-[#D94E18]" />
                <div className="w-8 h-px bg-stone-300" />
              </div>

              <div className="w-44 bg-white rounded-2xl border-2 border-[#D94E18]/40 shadow-sm shadow-[#D94E18]/10 p-4 relative">
                <div className="absolute -top-3 left-[4.5rem] w-7 h-7 rounded-full bg-[#D94E18] text-white flex items-center justify-center text-xs font-bold shadow-sm z-10" data-testid="step-2">2</div>
                <div className="absolute -top-2 -right-2 w-5 h-5 bg-[#D94E18] rounded-full flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-white" />
                </div>
                <div className="w-8 h-8 rounded-xl bg-[#D94E18] flex items-center justify-center mb-2.5">
                  <Brain className="w-4 h-4 text-white" />
                </div>
                <p className="text-[11px] font-bold text-stone-900 mb-0.5">AI Brain</p>
                <p className="text-[9px] text-stone-500 mb-2.5">Learns your patterns</p>
                <div className="space-y-1">
                  {['Classify & Learn', 'Apply Tax Rules', 'Match Accounts'].map((label) => (
                    <div key={label} className="bg-[#D94E18]/5 rounded py-1 px-2 text-center">
                      <p className="text-[9px] font-medium text-[#D94E18]">{label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col items-center justify-center self-center gap-1">
                <div className="w-8 h-px bg-stone-300" />
                <ArrowRight className="w-4 h-4 text-[#D94E18]" />
                <div className="w-8 h-px bg-stone-300" />
              </div>

              <div className="w-44 bg-white rounded-2xl border border-stone-200 shadow-sm p-4 relative">
                <div className="absolute -top-3 left-[4.5rem] w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold shadow-sm z-10" data-testid="step-3">3</div>
                <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center mb-2.5">
                  <BarChart3 className="w-4 h-4 text-emerald-600" />
                </div>
                <p className="text-[11px] font-bold text-stone-900 mb-0.5">Reports ready</p>
                <p className="text-[9px] text-stone-500 mb-2.5">P&L, Balance Sheet, VAT</p>
                <div className="flex items-end gap-0.5 h-12 px-1">
                  {[35, 55, 40, 72, 48, 65, 85, 52, 68, 45].map((h, idx) => (
                    <div key={`bar-${h}`} className="flex-1 rounded-sm" style={{ height: `${h}%`, backgroundColor: idx === 6 ? '#D94E18' : idx % 2 === 0 ? 'rgba(217,78,24,0.35)' : '#d6d3d1' }} />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5 mt-5">
              <div className="h-px w-6 bg-stone-300" />
              <p className="text-[10px] text-stone-500 font-medium tracking-wide uppercase">AI-powered pipeline — gets smarter with every upload</p>
              <div className="h-px w-6 bg-stone-300" />
            </div>
          </div>
        </div>

        {/* Trust indicators — higher contrast */}
        <div className="mt-16 pt-8 border-t border-stone-200 flex flex-wrap items-center justify-center gap-8 text-sm text-stone-600" data-testid="trust-indicators">
          <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-stone-500" /><span className="font-medium">32 countries, auto tax rates</span></div>
          <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-stone-500" /><span className="font-medium">Bank-grade encryption</span></div>
          <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-stone-500" /><span className="font-medium">Your data stays yours</span></div>
          <div className="flex items-center gap-2"><Brain className="w-4 h-4 text-stone-500" /><span className="font-medium">AI that learns your business</span></div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════
   PROBLEM  (dark — user likes this treatment)
   ══════════════════════════════════════════════════ */
function ProblemSection() {
  const frustrations = [
    { icon: Clock, line: 'Waiting weeks for your own numbers', detail: 'Reports arrive long after the month ends. By the time you see them, decisions have already been made without data.' },
    { icon: HelpCircle, line: "Not knowing where your money goes", detail: '"I don\'t know where it\'s all going." The most common financial frustration — and the hardest to answer without the right tools.' },
    { icon: Wallet, line: 'Paying specialists for basic visibility', detail: "You shouldn't need a bookkeeper just to understand your own spending. But somehow, that's the norm." },
    { icon: MessageCircle, line: 'Chasing people for your own data', detail: "Follow-ups, callbacks, month-end delays. Your financial position shouldn't depend on someone else's schedule." },
  ];

  return (
    <section id="problem" data-testid="problem-section" className="py-20 md:py-28 bg-stone-950 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />
      <div className="relative max-w-5xl mx-auto px-6">
        <div className="max-w-2xl">
          <SectionLabel icon={Zap} dark>The problem</SectionLabel>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl ${heading} text-white leading-snug`} style={hFont} data-testid="problem-headline">
            Knowing your numbers shouldn't be this hard
          </h2>
          <p className="mt-5 text-base text-stone-300 leading-relaxed max-w-xl">
            The financial world took something fundamentally simple — <em className="text-white not-italic font-semibold">"where is my money going?"</em> — and wrapped it in complexity, jargon, and gatekeepers.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
          {frustrations.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.line} data-testid={`problem-card-${f.line}`} className="group p-6 rounded-2xl border border-stone-800 bg-stone-900/60 hover:border-stone-700 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-stone-800 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D94E18]/10 transition-colors">
                    <Icon className="w-5 h-5 text-stone-500 group-hover:text-[#D94E18] transition-colors" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-stone-100" style={hFont}>{f.line}</p>
                    <p className="text-sm text-stone-400 mt-1.5 leading-relaxed">{f.detail}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-14 flex items-start gap-3 max-w-xl">
          <div className="w-1 rounded-full bg-[#D94E18] self-stretch flex-shrink-0" />
          <p className="text-sm text-stone-300 leading-relaxed">
            Whether you're managing personal finances or running a business — the frustration is the same. <span className="text-white font-bold">What if AI could do all of this for you, instantly?</span>
          </p>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════
   AI INTELLIGENCE
   ══════════════════════════════════════════════════ */
function AIIntelligenceSection() {
  const capabilities = [
    { icon: Brain, title: 'An AI brain that learns your business', desc: 'Correct a classification once, and Kaya remembers. The more you use it, the more accurate it gets. Your corrections carry 3x weight — the AI adapts to how you think about your finances.', accent: true },
    { icon: Globe, title: '32 countries. Auto tax intelligence.', desc: "Sign up from Spain? You get IVA rates. From France? TVA. Australia? GST. Kaya auto-configures the right tax rules, rates, currency, and compliance defaults for your country." },
    { icon: Mic, title: 'Talk to your finances', desc: '"Where am I spending the most this quarter?" Just ask. Kaya understands natural language — voice or text — and answers with real data from your books.' },
    { icon: Camera, title: 'Snap a receipt. Done.', desc: 'Take a photo of any receipt or invoice. Kaya extracts the data, matches the merchant, applies the right account and tax rate — all in seconds.' },
    { icon: Sparkles, title: 'Proactive financial insights', desc: "Kaya doesn't just wait for questions. It spots unusual spending, identifies trends, and surfaces insights you'd otherwise miss — like having a CFO watching your back." },
    { icon: RefreshCw, title: 'Self-improving accuracy', desc: 'Every transaction processed makes the system smarter. Merchant patterns, account matching, category predictions — all improve automatically with weighted machine learning.' },
  ];

  return (
    <section id="ai-intelligence" data-testid="ai-intelligence-section" className="py-20 md:py-28 bg-[#FAFAF9] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D94E18]/20 to-transparent" />

      <div className="relative max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <SectionLabel icon={Brain}>AI Intelligence</SectionLabel>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl ${heading} text-stone-900 leading-snug`} style={hFont}>
            Not just automation.<br />
            <span className="text-[#D94E18]">Intelligence that grows with you.</span>
          </h2>
          <p className="mt-4 text-base text-stone-600 max-w-xl mx-auto leading-relaxed">
            Most tools process transactions. Kaya understands them — and gets better at it every single day.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {capabilities.map((c) => (
            <FeatureCard key={c.title} icon={c.icon} title={c.title} desc={c.desc} accent={c.accent} />
          ))}
        </div>

        {/* Comparison strip */}
        <div className="mt-16 bg-white rounded-2xl border border-stone-200 p-8 max-w-4xl mx-auto" data-testid="comparison-strip">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-4">Traditional bookkeeping</p>
              <div className="space-y-2.5">
                {['Manual data entry — hours per week', 'Static rules, no learning', 'Reports in weeks, not seconds', 'One-size-fits-all tax setup', 'Silent until month-end'].map((item) => (
                  <div key={item} className="flex items-start gap-2.5">
                    <X className="w-4 h-4 text-stone-300 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-stone-500">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-[#D94E18] uppercase tracking-wider mb-4">kaya.ai</p>
              <div className="space-y-2.5">
                {['AI reads, classifies, posts — in seconds', 'Learns from every correction you make', 'Reports generated instantly after upload', '32-country auto tax intelligence', 'Proactive insights & voice assistant'].map((item) => (
                  <div key={item} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-[#D94E18] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <p className="text-sm text-stone-800 font-medium">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════
   FEATURES
   ══════════════════════════════════════════════════ */
function FeaturesSection() {
  const features = [
    { icon: Upload, title: 'Multi-format upload', desc: 'PDF statements, CSV exports, or snap a photo. Kaya reads them all — OCR included.' },
    { icon: Brain, title: 'AI double-entry journals', desc: 'Every transaction becomes a balanced journal entry with debits, credits, and automatic VAT splitting.' },
    { icon: BarChart3, title: '7 financial reports', desc: 'P&L, Balance Sheet, Cash Flow, VAT, Trial Balance, General Ledger, Expense Analysis — all instant.' },
    { icon: Eye, title: 'Financial health dashboard', desc: 'Live cash balance, monthly trends, top expenses — your entire financial picture in one glance.' },
    { icon: Smartphone, title: 'Works on your phone', desc: 'Install as an app. Share bank statements directly from your banking app into Kaya. Full PWA support.' },
    { icon: TrendingUp, title: 'CFO heartbeat report', desc: 'Monthly burn rate, runway forecast, revenue trends, YoY comparison — executive-grade analytics.' },
  ];

  return (
    <section id="features" data-testid="features-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <SectionLabel icon={Zap}>Features</SectionLabel>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl ${heading} text-stone-900 leading-snug`} style={hFont}>
            Everything your books need — AI-powered
          </h2>
          <p className="mt-4 text-base text-stone-600 max-w-xl mx-auto leading-relaxed">
            From raw bank data to audit-ready financials. Every feature built with intelligence at the core.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f) => (
            <FeatureCard key={f.title} icon={f.icon} title={f.title} desc={f.desc} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════
   HOW IT WORKS
   ══════════════════════════════════════════════════ */
function HowItWorksSection() {
  const steps = [
    { icon: Upload, num: '1', title: 'Upload your bank statement', desc: 'Drag a PDF, paste a CSV, or snap a photo of a receipt. Works with any bank worldwide.' },
    { icon: Brain, num: '2', title: 'AI classifies & learns', desc: "Kaya's AI reads every transaction, matches merchants, applies tax rules, and creates double-entry journals. Corrections make it smarter." },
    { icon: BarChart3, num: '3', title: 'Reports are instant', desc: 'P&L, Balance Sheet, VAT, Cash Flow — all generated automatically. Ask the AI any question about your finances.' },
  ];

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-20 md:py-28 bg-[#FAFAF9] border-y border-stone-200">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <SectionLabel icon={ArrowRight}>How it works</SectionLabel>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl ${heading} text-stone-900 leading-snug`} style={hFont}>
            Three steps. Zero manual work.
          </h2>
          <p className="mt-4 text-base text-stone-600 leading-relaxed">
            Upload &rarr; AI processes &rarr; Reports ready. That's it.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
          {steps.map((s, idx) => {
            const Icon = s.icon;
            return (
              <div key={s.num} data-testid={`step-card-${s.num}`} className="relative flex flex-col items-center text-center px-8 py-8">
                {idx < 2 && (
                  <div className="hidden md:block absolute top-[3.25rem] left-[calc(50%+2rem)] right-[calc(-50%+2rem)] h-px bg-stone-300">
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#D94E18]" />
                  </div>
                )}
                <div className="relative w-16 h-16 rounded-2xl bg-[#D94E18]/[0.07] flex items-center justify-center mb-5">
                  <Icon className="w-7 h-7 text-[#D94E18]" strokeWidth={1.5} />
                  <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-stone-900 text-white text-xs font-bold flex items-center justify-center">{s.num}</div>
                </div>
                <h3 className="text-sm font-bold text-stone-900 mb-2" style={hFont}>{s.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed max-w-xs">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════
   PRICING
   ══════════════════════════════════════════════════ */
function PricingSection() {
  const plans = [
    { name: 'Free', price: '$0', period: '/mo', desc: 'Try it out.', features: ['200 credits / quarter', '1 organization', '1 user', '15 AI chatbot msgs / mo'], cta: 'Get started', featured: false },
    { name: 'Starter', price: '$9.99', period: '/seat/mo', desc: 'Get serious.', features: ['500 credits / seat / month', '1 organization', 'Unlimited team members', '30 AI chatbot msgs / mo', 'Top-ups + overage'], cta: 'Start now', featured: false },
    { name: 'Pro', price: '$29.99', period: '/seat/mo', desc: 'Full AI power.', features: ['2,000 credits / seat / month', '3 organizations', 'Unlimited team members', '100 AI chatbot msgs / mo', 'Overage at $0.05/cr'], cta: 'Start now', featured: true },
    { name: 'Business', price: '$99.99', period: '/seat/mo', desc: 'Scale up.', features: ['7,000 credits / seat / month', '5 organizations', 'Unlimited team members', '500 AI chatbot msgs / mo', 'Priority AI processing'], cta: 'Start now', featured: false },
    { name: 'Enterprise', price: '$199.99', period: '/seat/mo', desc: 'Multi-entity ops.', features: ['30,000 credits / seat / month', '10 organizations', 'Unlimited team members', '2,000 AI chatbot msgs / mo', 'Priority AI processing'], cta: 'Start now', featured: false },
  ];

  return (
    <section id="pricing" data-testid="pricing-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <SectionLabel icon={Wallet}>Pricing</SectionLabel>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl ${heading} text-stone-900 leading-snug`} style={hFont}>
            Simple, transparent pricing
          </h2>
          <p className="mt-4 text-base text-stone-600 leading-relaxed">
            Every AI feature from day one. Credits only meter AI processing — your data, reports, and records are always yours.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-7xl mx-auto">
          {plans.map((plan) => (
            <div key={plan.name} data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              className={`rounded-2xl p-6 flex flex-col transition-all duration-300 ${
                plan.featured
                  ? 'bg-stone-900 text-white ring-2 ring-[#D94E18] shadow-xl relative'
                  : 'bg-white border border-stone-200 hover:border-stone-300 hover:shadow-md'
              }`}>
              {plan.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D94E18] text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap">Most popular</div>
              )}
              <div className="mb-5">
                <h3 className={`text-xs font-bold mb-2 uppercase tracking-wider ${plan.featured ? 'text-stone-400' : 'text-stone-500'}`} style={hFont}>{plan.name}</h3>
                <div className="flex items-baseline gap-0.5">
                  <span className={`text-3xl font-bold ${plan.featured ? 'text-white' : 'text-stone-900'}`} style={monoFont}>{plan.price}</span>
                  {plan.period && <span className={`text-sm font-medium ${plan.featured ? 'text-stone-400' : 'text-stone-500'}`}>{plan.period}</span>}
                </div>
                <p className={`text-sm mt-2 leading-snug ${plan.featured ? 'text-stone-400' : 'text-stone-600'}`}>{plan.desc}</p>
              </div>
              <ul className="space-y-2.5 mb-6 flex-1">
                {plan.features.map((feat) => (
                  <li key={feat} className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#D94E18]" strokeWidth={2.5} />
                    <span className={`text-sm leading-snug ${plan.featured ? 'text-stone-300' : 'text-stone-700'}`}>{feat}</span>
                  </li>
                ))}
              </ul>
              <Link to="/login?register=true" data-testid={`pricing-cta-${plan.name.toLowerCase()}`}>
                <Button className={`w-full h-10 rounded-full text-sm font-bold transition-all ${
                  plan.featured
                    ? 'bg-[#D94E18] hover:bg-[#C2410C] text-white'
                    : 'bg-stone-900 hover:bg-stone-800 text-white'
                }`}>
                  {plan.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-stone-400 mt-6">*Auto-approval subject to minimum usage threshold.</p>

        <div className="mt-12 max-w-2xl mx-auto text-center">
          <p className="text-sm font-bold text-stone-800 mb-1" style={hFont}>Credit top-ups</p>
          <p className="text-sm text-stone-500 mb-5">Available on all paid plans.</p>
          <div className="flex justify-center gap-4 flex-wrap">
            {[{ credits: 50, price: '$5' }, { credits: 150, price: '$12' }, { credits: 500, price: '$30' }].map((pack) => (
              <div key={pack.credits} data-testid={`topup-pack-${pack.credits}`} className="px-6 py-4 rounded-2xl border border-stone-200 bg-white text-center hover:border-stone-300 hover:shadow-md transition-all duration-300">
                <div className="text-xl font-bold text-stone-900" style={monoFont}>{pack.credits}</div>
                <div className="text-xs text-stone-500 mt-0.5">credits</div>
                <div className="text-sm font-bold text-[#D94E18] mt-2" style={monoFont}>{pack.price}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-stone-500 mt-4">Purchased credits never expire. Used after monthly allocation.</p>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════
   CTA  (dark — matches Problem section)
   ══════════════════════════════════════════════════ */
function CTASection() {
  return (
    <section className="py-20 md:py-28 bg-stone-950 relative overflow-hidden" data-testid="cta-section">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />
      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <SectionLabel icon={Brain} dark>AI that gets smarter with every upload</SectionLabel>
        <h2 className={`text-2xl sm:text-3xl lg:text-4xl ${heading} text-white leading-snug`} style={hFont}>
          Stop chasing your numbers.<br />
          <span className="text-[#D94E18]">Let AI handle it.</span>
        </h2>
        <p className="mt-5 text-base text-stone-400 leading-relaxed max-w-lg mx-auto">
          Upload your first bank statement and let Kaya classify every transaction, apply the right tax rules, and produce your financial reports — in minutes, not months.
        </p>
        <div className="mt-8">
          <Link to="/login?register=true" data-testid="cta-bottom-btn">
            <Button className="bg-[#D94E18] hover:bg-[#C2410C] text-white h-12 px-10 text-sm font-semibold rounded-full shadow-lg shadow-[#D94E18]/20">
              Start for free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
        <p className="mt-4 text-xs text-stone-500">No credit card required. Works with any bank in 32 countries.</p>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════
   FOOTER
   ══════════════════════════════════════════════════ */
function Footer() {
  return (
    <footer data-testid="landing-footer" className="border-t border-stone-200 bg-white">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row justify-between gap-8">
          <div className="max-w-xs">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-[#D94E18] rounded-lg flex items-center justify-center">
                <Building2 className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-bold text-stone-900" style={hFont}>kaya.ai</span>
            </div>
            <p className="text-sm text-stone-500 leading-relaxed">
              AI-powered accounting that learns your business. Upload, classify, report — all automated, getting smarter every day.
            </p>
          </div>
          <div className="flex gap-16">
            <div>
              <h4 className="text-xs font-bold text-stone-900 uppercase tracking-wider mb-3" style={hFont}>Product</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">Features</a></li>
                <li><a href="#pricing" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">Pricing</a></li>
                <li><a href="#how-it-works" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">How it works</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold text-stone-900 uppercase tracking-wider mb-3" style={hFont}>Solutions</h4>
              <ul className="space-y-2">
                <li><Link to="/for/solo-founders" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">Solo Founders</Link></li>
                <li><Link to="/for/startups" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">Startups & Scale-ups</Link></li>
                <li><Link to="/for/growing-teams" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">Growing Teams</Link></li>
                <li><Link to="/for/finance-pros" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">Finance Professionals</Link></li>
                <li><Link to="/blog" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold text-stone-900 uppercase tracking-wider mb-3" style={hFont}>Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/terms" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/privacy" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-stone-100 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-stone-500">&copy; {new Date().getFullYear()} kaya.ai. All rights reserved.</p>
          <p className="text-xs text-stone-500">An <a href="https://inopsia.ai" target="_blank" rel="noopener noreferrer" className="text-[#D94E18] hover:underline font-medium">inopsia.ai</a> company</p>
        </div>
      </div>
    </footer>
  );
}

/* ══════════════════════════════════════════════════
   PAGE COMPOSITION
   ══════════════════════════════════════════════════ */
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="landing-page">
      <NavBar />
      <HeroSection />
      <SocialProofStrip />
      <ProblemSection />
      <AIIntelligenceSection />
      <FeaturesSection />
      <HowItWorksSection />
      <DemoSection />
      <SavingsCalculator />
      <PricingSection />
      <CTASection />
      <Footer />
      <ChatWidget />
    </div>
  );
}
